import type { ReactNode } from 'react';

/**
 * Shared shell for `/login` and `/logout` pages. Ports the common bits
 * duplicated across `templates/auth/login.html` and `logged_out.html`:
 *   - Body bg = `--light-bg`, Gill Sans MT font.
 *   - `.hero-image` at opacity 0.08 behind the card.
 *   - Translucent-white `.login-container` / `.logout-container` card.
 *   - `.fulcrum-logo` pill wrapping a 48px Fulcrum logo.
 *
 * Raw-hex policy (matches the templates' inline CSS verbatim):
 *   - `#F9FAFB` body bg                → `var(--light-bg)` (exact duplicate).
 *   - `#FFFFFF` / `white` (logo panel) → `var(--text-light)` (exact duplicate).
 *   - Other hex values (#23272F, #4B5563, #374151, #1F2937, #991B1B, #E5E7EB)
 *     have no var equivalent in the 8-token palette and are kept verbatim
 *     in the caller's markup.
 *   - Translucent rgba() shadows and card bg are kept verbatim (alpha tokens
 *     not covered by the 8 vars).
 *
 * Hero spiral uses `/static/logos/image.png` (local, served by Flask)
 * matching `PageHeader.LOCAL_HERO_IMAGE`, never the external CDN URL.
 */

const LOGO_SRC = '/static/logos/logo.png';
const HERO_SRC = '/static/logos/image.png';

export interface AuthShellProps {
  children: ReactNode;
  /** Optional extra text alignment inside the card (default: centered column). */
  centered?: boolean;
}

export default function AuthShell({ children, centered = false }: AuthShellProps): JSX.Element {
  return (
    <section
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{ background: 'var(--light-bg)', fontFamily: 'var(--font-body)' }}
    >
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{ opacity: 0.08 }}
        aria-hidden="true"
      >
        <img
          src={HERO_SRC}
          alt=""
          className="mx-auto block h-auto"
          style={{ width: '70vw', minWidth: 320, maxWidth: 700 }}
        />
      </div>

      <div
        className={`relative z-10 flex flex-col items-center${centered ? ' text-center' : ''}`}
        style={{
          maxWidth: 380,
          width: '100%',
          padding: '2rem 2rem 1.75rem 2rem',
          background: 'rgba(255, 255, 255, 0.98)',
          borderRadius: 14,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div
          className="flex items-center justify-center"
          style={{
            background: 'var(--text-light)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            marginBottom: '0.7rem',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}
        >
          <img
            src={LOGO_SRC}
            alt="Fulcrum Asset Management"
            style={{ height: 48, width: 'auto', display: 'block' }}
          />
        </div>
        {children}
      </div>
    </section>
  );
}
